import { useEffect, useRef, useState } from 'react'
import { FilterMatchMode } from 'primereact/api'
import { OverlayPanel } from 'primereact/overlaypanel'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

// local imports
import { CampusServiceTypes, ICampusServiceReservations } from 'types/TrackingType'
import { saveAsExcelFile } from 'utils/Miscellaneous'
import { IRemoteReservationResponse, ReservationStatus } from '../../../types/RemoteReservationRequest'

export default function useNotificationTable(
  data: IRemoteReservationResponse[] | undefined,
  selectedStatus: ReservationStatus[]
) {
  const { t } = useTranslation()

  /* States - Variables */
  const [tableData, setTableData] = useState<IRemoteReservationResponse[] | undefined>([])
  const [filters, setFilters] = useState<any>(null)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [notification, setNotification] = useState(false)
  const [approveNotification, setApproveNotification] = useState(false)
  const [rejectNotification, setRejectNotification] = useState(false)
  const [selectedRow, setSelectedRow] = useState<IRemoteReservationResponse | undefined>()
  const addToExcel = useRef<OverlayPanel>(null)

  /* initiate filters */
  useEffect(() => {
    const initFilters = () => {
      setFilters({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        reservationStatusId: { value: null, matchMode: FilterMatchMode.EQUALS },
      })
    }
    initFilters()
  }, [])

  /* Manipulate response data and set states => (active - canceled reservations) */
  const handleData = () => {
    const selecteTypesOnly = data?.filter((rd) => selectedStatus.includes(rd.reservationStatusId as ReservationStatus))
    const activeData = selecteTypesOnly?.filter((datum) => datum.reservationStatusId !== 3)
    setTableData(selecteTypesOnly)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleData(), [selectedStatus, data])

  /* ################ Tabs  ################*/
  const [tabIndex, setTabIndex] = useState(0)
  const handleTabChange = (e: any) => setTabIndex(e.index)

  const isStatusOptions = [
    { label: t('request-remote-reservation.pending-approval'), value: 1 },
    { label: t('request-remote-reservation.approved'), value: 2 },
    { label: t('request-remote-reservation.rejected'), value: 3 },
  ]
  const hasItems = !!tableData?.length

  /* ############# Excel Export ############# */
  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const Heading = [
        [
          `${t('visitor.name')} ${t('visitor.surname')}`,
          t('visitor.email'),
          t('request-remote-reservation.request-date'),
          t('request-remote-reservation.reservation-date'),
          t('request-remote-reservation.request-count'),
          t('request-remote-reservation.status'),
        ],
      ]

      const excData = tableData
      const fileName = t('request-remote-reservation.notifications')

      /** TODO: follow issues
       * Global filtered values are not accessible in the datatable, currently there is no callback invoked when a global filter is applied.
       * => https://github.com/primefaces/primereact/issues/1763
       * => https://github.com/primefaces/primereact/issues/1763
       */
      const excelData = excData?.map((res) => {
        const lookup: Record<number, string> = {
          1: 'request-remote-reservation.pending-approval',
          2: 'request-remote-reservation.approved',
          3: 'request-remote-reservation.rejected',
        }
        let temp = {
          EmployeeName: `${res.employeeName} ${res.employeeSurname}`,
          EmployeeEmail: res.email,
          RequestDate: moment(res.requestDate).format('DD/MM/YYYY'),
          ReservationDate: moment(res.reservationDate).format('DD/MM/YYYY'),
          RequestCount: res.notificationCount,
          ReservationStatus: t(lookup[res.reservationStatusId]),
        }
        return temp
      })

      const ws = xlsx.utils.book_new()
      xlsx.utils.sheet_add_aoa(ws, Heading)
      const worksheet = xlsx.utils.sheet_add_json(ws, excelData!, { origin: 'A2', skipHeader: true })
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' })
      saveAsExcelFile(excelBuffer, fileName)
    })
  }
  const showExcel = (e: React.MouseEvent<HTMLDivElement>) => addToExcel.current?.show(e, e.target)
  /* ############# Excel Export ############# */

  /* Global table search (name, surname) */
  const onGlobalFilterChange = (input: any) => {
    const value = input
    let _filters = { ...filters }
    _filters['global'].value = value
    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  return {
    tableData,
    t,
    filters,
    tabIndex,
    addToExcel,
    isStatusOptions,
    globalFilterValue,
    notification,
    setNotification,
    approveNotification,
    setApproveNotification,
    rejectNotification,
    setRejectNotification,
    selectedRow,
    setSelectedRow,
    handleTabChange,
    hasItems,
    exportExcel,
    showExcel,
    onGlobalFilterChange,
  }
}
